import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import JsonView from 'react18-json-view';
import { IoMdRefresh } from 'react-icons/io';
import { unique } from 'radash';
import { useJob, useEvals } from '../state';
import { styles } from '../constants';
import { Loading } from '../components/loading';
import { Button, PillButtons } from '../components/input';
import { Modal } from '../components/modal';
import { Results } from '../components/agent';
import { Collapsible } from '../components/collapsible';

const cellStyle = { fontSize: 12, padding: '2px 4px' };

const SingleEval = ({ ev }) => {
  const [tab, setTab] = useState('results');
  const { job } = useJob(ev.jobId);

  const choices = [
    ['results', 'Results'],
    ['json', 'JSON'],
  ];

  const resultsNode = (
    <div>
      {!job && <Loading />}
      {job && <Results job={job} limit={20} />}
    </div>
  );

  const jsonNode = <JsonView src={ev} />;

  return (
    <div>
      <div style={{ marginTop: 10 }}>
        <Button small grayFlat to={`/${job.name == 'agent' ? 'agents' : 'jobs'}/${job.jobId}`} target="_blank">
          Open {job.jobId}
        </Button>
      </div>
      <p>{ev.reasoning.reasoning}</p>
      <PillButtons choices={choices} value={tab} onChange={setTab} />
      <br />
      {tab == 'results' && resultsNode}
      {tab == 'json' && jsonNode}
    </div>
  );
};

const EvalDetails = ({ evals }) => {
  const nodes = evals.map((ev) => {
    return (
      <Collapsible
        key={ev.jobId}
        label={`${ev.rating}, \$${(parseFloat(ev.cost) || 0).toFixed(4)}, ${(parseFloat(ev.runtime) / 1000).toFixed(1)}s, ${ev.out?.results?.items?.length} items`}
      >
        <SingleEval ev={ev} />
      </Collapsible>
    );
  });
  return <div style={{ width: 800, display: 'flex', flexDirection: 'column', gap: 10 }}>{nodes}</div>;
};

const Evals = ({ evals, field, env, groupBy, aggr }) => {
  const [active, setActive] = useState();

  const names = {};
  const groups = [];
  const scores = {};

  const messages = {};

  const matching = evals
    .filter((it) => !!it.config?.name)
    .filter((it) => it.env == env)
    .filter((it) => it.config?.nonce != 'stable')
    .sort((a, b) => b.id - a.id);

  for (const ev of matching) {
    const name = ev.config.name;

    let group;

    const date = ev.createdAt
      .split(' ')[0]
      .split('-')
      .slice(1, 3)
      .map((it) => it.replace(/^0/, ''))
      .join('/');

    if (groupBy == 'date') {
      group = date;
    } else if (groupBy == 'commit') {
      group = (ev.config.commit || '').substring(0, 7);
      if (ev.config.message) {
        messages[group] = ev.config.message;
      }
    } else {
      group = '??';
    }

    if (!group) {
      continue;
    }

    if (!groups.includes(group)) {
      groups.push(group);
    }

    names[name] = true;
    // groups[group] = true;
    scores[name] ||= {};
    scores[name][group] ||= {
      ratings: [],
      runtimes: [],
      costs: [],
      jobIds: [],
    };
    scores[name][group].jobIds.push(ev.jobId);
    try {
      scores[name][group].ratings.push(parseFloat(ev.rating));
    } catch {}
    try {
      scores[name][group].runtimes.push(parseFloat(ev.runtime));
    } catch {}
    try {
      scores[name][group].costs.push(parseFloat(ev.cost));
    } catch {}
  }

  const width = 60;
  const nameWidth = 180;

  const headers = [
    <th key="name" style={{ ...cellStyle, width: nameWidth }}>
      name
    </th>,
  ];

  headers.push(
    ...groups.map((h) => (
      <th key={h} style={{ ...cellStyle, width }} data-tooltip-id={`th-${h}`} data-tooltip-content={messages[h]}>
        {h}
        <Tooltip id={`th-${h}`} place="top" style={{ maxWidth: 400, fontSize: 12 }} />
      </th>
    )),
  );

  console.log('messages', messages);

  const rows = Object.keys(names).map((name) => {
    const cells = [
      <td key="name" style={{ ...cellStyle, width: nameWidth }}>
        {name}
      </td>,
    ];
    for (const group of groups) {
      const a = name + ':' + group;
      const hover = a == active;

      let cell;
      const score = scores[name][group];
      if (score) {
        const latest = (l) => l[0];

        const avg = (l) => {
          const f = (l || []).filter((it) => it >= 0);
          return f.reduce((acc, x) => acc + x, 0) / (f.length || 1);
        };

        const median = (l) => {
          const f = (l || []).filter((it) => it >= 0).sort((a, b) => a - b);
          const n = f.length;
          if (!n) return 0;
          const mid = Math.floor(n / 2);
          return n % 2 ? f[mid] : (f[mid - 1] + f[mid]) / 2;
        };

        const min = (l) => {
          const f = (l || []).filter((it) => it >= 0);
          return f.length ? Math.min(...f) : 0;
        };

        const max = (l) => {
          const f = (l || []).filter((it) => it >= 0);
          return f.length ? Math.max(...f) : 0;
        };

        const aggrFn = {
          latest,
          avg,
          median,
          min,
          max,
        }[aggr];

        const body = {
          rating: aggrFn(score.ratings).toFixed(0),
          runtime: (aggrFn(score.runtimes) / 1000).toFixed(1) + 's',
          cost: '$' + aggrFn(score.costs).toFixed(4),
        }[field];

        const details = {
          rating: score.ratings.join(', '),
          runtime: score.runtimes.map((it) => (it / 1000).toFixed(1) + 's').join(', '),
          cost: score.costs.map((it) => '$' + it.toFixed(4)).join(', '),
        }[field];

        const popup = hover ? (
          <div
            style={{
              position: 'absolute',
              border: '1px solid #888',
              background: 'white',
              padding: 5,
              marginTop: 5,
              zIndex: 1,
            }}
          >
            {details}
          </div>
        ) : null;

        const evs = matching.filter((it) => score.jobIds.includes(it.jobId));

        cell = (
          <td
            style={{ ...cellStyle, width }}
            key={group}
            onMouseEnter={() => setActive(a)}
            onMouseLeave={() => setActive()}
          >
            <Modal>
              <div style={{ textAlign: 'center' }}>{body}</div>
              <div>
                <EvalDetails evals={evs} />
              </div>
            </Modal>
            {popup}
          </td>
        );
      } else {
        cell = <td style={{ ...cellStyle, width }} key={group}></td>;
      }
      cells.push(cell);
    }
    return <tr key={name}>{...cells}</tr>;
  });

  return (
    <div>
      {env}: {field}
      <table style={{ tableLayout: 'fixed', width: headers.length * width }}>
        <thead>
          <tr>{headers}</tr>
        </thead>
        <tbody>{rows}</tbody>
      </table>
    </div>
  );
};

const useQueryState = (key, defaultValue) => {
  const get = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get(key) || defaultValue;
  };

  const [value, setValue] = useState(get);

  const setAndPersist = (next) => {
    const params = new URLSearchParams(window.location.search);
    if (next == null) {
      params.delete(key);
    } else {
      params.set(key, next);
    }

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);
    setValue(next);
  };

  // handle back/forward navigation
  useEffect(() => {
    const handler = () => setValue(get());
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  return [value, setAndPersist];
};

export default function EvalsDashboardPage() {
  const { evals, update, loading } = useEvals();

  const [groupBy, setGroupBy] = useQueryState('groupBy', 'date');
  const [env, setEnv] = useQueryState('env', 'prod');
  const [aggr, setAggr] = useQueryState('aggr', 'latest');

  if (!evals) {
    return <Loading />;
  }

  const groupBys = [['date'], ['commit']];
  const envs = [['prod'], ['staging']];
  const aggrs = [['latest'], ['median'], ['avg'], ['min'], ['max']];

  const control = (label, choices, value, setValue) => (
    <>
      <div>
        <b>{label}:</b>
      </div>
      <PillButtons size="sm" choices={choices} value={value} onChange={setValue} />
    </>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, marginBottom: 10 }}>
        <Button small onClick={update} loading={loading} style={{ width: 40 }} grayFlat>
          <IoMdRefresh size={18} />
        </Button>
        {control('Group by', groupBys, groupBy, setGroupBy)}
        {control('Env', envs, env, setEnv)}
        {control('Aggr', aggrs, aggr, setAggr)}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Evals evals={evals} field="rating" env={env} groupBy={groupBy} aggr={aggr} />
        <Evals evals={evals} field="runtime" env={env} groupBy={groupBy} aggr={aggr} />
        <Evals evals={evals} field="cost" env={env} groupBy={groupBy} aggr={aggr} />
      </div>
    </div>
  );
}
